import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Trash2,
  Plus,
  Minus,
  ShoppingBag,
  Truck,
  ShieldCheck,
  Heart,
  Info,
  Ticket,
  Lock,
  RotateCcw,
  Headphones,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  UserRound,
  BadgeCheck,
} from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { listProducts } from "@/lib/adapters/products";
import ProductCard from "@/components/product/ProductCard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const DELIVERY_THRESHOLD = 999;
const DCR_STORAGE_KEY = "dyntra_dcr_id_v1";

export default function Cart() {
  const { cart, updateQty, removeItem } = useCart();
  const { user, setAuthOpen, setAuthMode } = useAuth();
  const { toggleWishlist } = useWishlist();
  const nav = useNavigate();

  const [selected, setSelected] = useState({});
  const [agree, setAgree] = useState(true);
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [dcrDialog, setDcrDialog] = useState(false);
  const [dcrId, setDcrId] = useState(
    () => localStorage.getItem(DCR_STORAGE_KEY) || ""
  );
  const [dcrInput, setDcrInput] = useState("");
  const [suggested, setSuggested] = useState([]);
  const [carouselIdx, setCarouselIdx] = useState(0);

  // Every time cart items refresh, ensure new items become selected by default.
  useEffect(() => {
    setSelected((prev) => {
      const next = { ...prev };
      for (const it of cart.items) {
        if (!(it.product.id in next)) next[it.product.id] = true;
      }
      return next;
    });
  }, [cart.items]);

  // Load "You may also like" strip.
  useEffect(() => {
    listProducts({ limit: 12 })
      .then(({ items }) => setSuggested(items))
      .catch(() => setSuggested([]));
  }, []);

  const selectedCount = cart.items.filter((it) => selected[it.product.id]).length;
  const totalCount = cart.items.length;

  const selectedTotals = useMemo(() => {
    let mrp = 0;
    let price = 0;
    for (const it of cart.items) {
      if (!selected[it.product.id]) continue;
      mrp += (it.product.mrp || it.product.price) * it.quantity;
      price += it.product.price * it.quantity;
    }
    return { mrp, price, discount: Math.max(0, mrp - price) };
  }, [cart.items, selected]);

  const couponDiscount = appliedCoupon?.value || 0;
  const grandTotal = Math.max(0, selectedTotals.price - couponDiscount);
  const totalSaved = selectedTotals.discount + couponDiscount;
  const deliveryUnlocked = selectedTotals.price >= DELIVERY_THRESHOLD;
  const remainingForFree = Math.max(0, DELIVERY_THRESHOLD - selectedTotals.price);
  const progressPct = Math.min(
    100,
    Math.round((selectedTotals.price / DELIVERY_THRESHOLD) * 100)
  );

  const toggleRow = (id) =>
    setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    const allOn = cart.items.every((it) => selected[it.product.id]);
    const next = {};
    cart.items.forEach((it) => (next[it.product.id] = !allOn));
    setSelected(next);
  };
  const removeSelected = () => {
    for (const it of cart.items) {
      if (selected[it.product.id]) removeItem(it.product.id);
    }
  };

  const moveToWishlist = (product) => {
    toggleWishlist(product);
    removeItem(product.id);
  };

  const applyCoupon = () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return;
    const flatMap = { SAVE20: Math.round(selectedTotals.price * 0.2), HEALTH15: Math.round(selectedTotals.price * 0.15), FLAT200: 200, WELLNESS10: Math.round(selectedTotals.price * 0.1) };
    if (!(code in flatMap)) {
      toast.error("Coupon code is invalid or expired.");
      return;
    }
    setAppliedCoupon({ code, value: Math.min(flatMap[code], selectedTotals.price) });
    toast.success(`Coupon ${code} applied`);
  };

  const saveDcr = () => {
    if (!dcrInput.trim()) {
      toast.error("Please enter a valid DCR ID.");
      return;
    }
    setDcrId(dcrInput.trim());
    localStorage.setItem(DCR_STORAGE_KEY, dcrInput.trim());
    setDcrDialog(false);
    toast.success("Dental Council Registration ID saved");
  };

  const proceed = () => {
    if (!agree) {
      toast.error("Please accept the Terms & Conditions.");
      return;
    }
    if (selectedCount === 0) {
      toast.error("Select at least one item to continue.");
      return;
    }
    if (!user) {
      setAuthMode?.("login");
      setAuthOpen?.(true);
      return;
    }
    nav("/checkout");
  };

  const openLogin = () => {
    setAuthMode?.("login");
    setAuthOpen?.(true);
  };

  if (!cart.items || cart.items.length === 0) {
    return (
      <main className="dyntra-container py-16 text-center">
        <ShoppingBag className="h-16 w-16 mx-auto text-gray-300 mb-4" />
        <h1 className="font-display text-2xl font-bold text-gray-900">
          Your cart is empty
        </h1>
        <p className="text-gray-500 mt-2">
          Browse products and add to cart to see them here.
        </p>
        <Link
          to="/shop"
          data-testid="cart-continue-shopping"
          className="dyntra-btn-primary mt-5 inline-flex"
        >
          Continue Shopping
        </Link>
      </main>
    );
  }

  return (
    <main className="dyntra-container py-6" data-testid="cart-page">
      <nav className="text-xs text-gray-500 mb-3">
        <Link to="/" className="hover:text-[#4B2AAD]">Home</Link>
        {" / "}
        <span className="text-gray-900 font-semibold">Cart</span>
      </nav>
      <h1
        data-testid="cart-title"
        className="font-display text-3xl font-extrabold text-gray-900"
      >
        My Cart{" "}
        <span className="text-[#4B2AAD] text-xl font-bold">
          ({cart.count} items)
        </span>
      </h1>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
        {/* LEFT COLUMN */}
        <div className="space-y-4">
          {/* Delivery progress */}
          <div className="rounded-2xl bg-[#F3EFFC] border border-[#4B2AAD]/15 p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center shrink-0">
              <Truck className="h-4 w-4 text-[#4B2AAD]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900">
                {deliveryUnlocked
                  ? "You've unlocked FREE delivery"
                  : `Add ₹${remainingForFree.toLocaleString()} more to unlock FREE delivery`}
              </p>
              <div className="mt-2 h-2 rounded-full bg-white/70 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#4B2AAD] to-[#6B4CD9]"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <p className="text-[11px] text-gray-600 mt-1">
                {deliveryUnlocked
                  ? "Great choice! Your order qualifies for free delivery."
                  : `₹${remainingForFree.toLocaleString()} more to enjoy FREE delivery`}
              </p>
            </div>
            {deliveryUnlocked && (
              <BadgeCheck className="h-6 w-6 text-emerald-600" />
            )}
          </div>

          {/* Items panel */}
          <div className="rounded-2xl border border-gray-100 bg-white">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <label className="flex items-center gap-2 text-sm font-bold text-gray-900">
                <input
                  type="checkbox"
                  checked={selectedCount > 0 && selectedCount === totalCount}
                  onChange={toggleAll}
                  className="h-4 w-4 accent-[#4B2AAD]"
                  data-testid="cart-select-all"
                />
                {selectedCount}/{totalCount} Items Selected
              </label>
              <button
                type="button"
                onClick={removeSelected}
                data-testid="cart-remove-selected"
                className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-red-500"
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove selected
              </button>
            </div>

            <div className="divide-y divide-gray-100">
              {cart.items.map((it) => (
                <CartRow
                  key={it.product.id}
                  item={it}
                  checked={!!selected[it.product.id]}
                  onToggle={() => toggleRow(it.product.id)}
                  onQty={(q) => updateQty(it.product.id, q)}
                  onRemove={() => removeItem(it.product.id)}
                  onMove={() => moveToWishlist(it.product)}
                />
              ))}
            </div>
          </div>

          <Link
            to="/shop"
            data-testid="cart-continue-link"
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:border-[#4B2AAD] hover:text-[#4B2AAD]"
          >
            ← Continue Shopping
          </Link>

          {/* You may also like */}
          <YouMayAlsoLike
            items={suggested}
            idx={carouselIdx}
            onPrev={() =>
              setCarouselIdx((i) => Math.max(0, i - 1))
            }
            onNext={() =>
              setCarouselIdx((i) =>
                Math.min(Math.max(0, suggested.length - 6), i + 1)
              )
            }
          />

          {/* USP strip */}
          <div className="rounded-2xl bg-white border border-gray-100 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Usp icon={ShieldCheck} title="100% authentic products" sub="Sourced directly from trusted brands." />
            <Usp icon={RotateCcw} title="10-day easy returns" sub="Hassle-free returns for eligible items." />
            <Usp icon={Lock} title="Secure payments" sub="Encrypted checkout and trusted payment options." />
            <Usp icon={Headphones} title="Dedicated support" sub="Expert help whenever you need it." />
          </div>
        </div>

        {/* RIGHT SIDEBAR */}
        <aside className="space-y-3">
          {user ? (
            <div className="rounded-2xl border border-gray-100 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-[#F3EFFC] flex items-center justify-center shrink-0">
                  <ShieldCheck className="h-5 w-5 text-[#4B2AAD]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 flex items-center gap-1">
                    Dental Council Registration ID <Info className="h-3.5 w-3.5 text-gray-400" />
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {dcrId ? (
                      <>Saved: <span className="font-semibold text-gray-800">{dcrId}</span></>
                    ) : (
                      "Add your Medical or Dental Council Registration ID to verify your professional credentials."
                    )}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setDcrInput(dcrId);
                    setDcrDialog(true);
                  }}
                  data-testid="cart-dcr-add"
                  className="dyntra-btn-primary text-xs py-1.5"
                >
                  {dcrId ? "Edit" : "Add"}
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-100 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-[#F3EFFC] flex items-center justify-center shrink-0">
                  <UserRound className="h-5 w-5 text-[#4B2AAD]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900">Shopping as a guest</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Log in to use saved details and continue securely.
                  </p>
                </div>
                <button
                  onClick={openLogin}
                  data-testid="cart-guest-login"
                  className="rounded-xl border border-[#4B2AAD]/40 text-[#4B2AAD] hover:bg-[#F3EFFC] text-xs font-bold px-3 py-1.5"
                >
                  Log In
                </button>
              </div>
            </div>
          )}

          {/* Coupon accordion */}
          <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => setCouponOpen((v) => !v)}
              data-testid="cart-coupon-toggle"
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-bold text-gray-900"
            >
              <span className="inline-flex items-center gap-2">
                <Ticket className="h-4 w-4 text-[#4B2AAD]" /> Apply coupon
              </span>
              {couponOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </button>
            {couponOpen && (
              <div className="px-4 pb-4 space-y-2">
                {appliedCoupon ? (
                  <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-xs">
                    <p className="font-semibold text-emerald-700">
                      Coupon <b>{appliedCoupon.code}</b> applied — you save ₹
                      {appliedCoupon.value.toLocaleString()}
                    </p>
                    <button
                      onClick={() => setAppliedCoupon(null)}
                      className="text-emerald-700 font-bold mt-1 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      placeholder="Enter code"
                      className="flex-1 rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm"
                    />
                    <button
                      onClick={applyCoupon}
                      className="dyntra-btn-primary text-xs py-1.5"
                      data-testid="cart-coupon-apply"
                    >
                      Apply
                    </button>
                  </div>
                )}
                <p className="text-[11px] text-gray-500">
                  Try <b>SAVE20</b> · <b>HEALTH15</b> · <b>FLAT200</b> · <b>WELLNESS10</b>
                </p>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="rounded-2xl border border-gray-100 bg-white p-4 space-y-3">
            <h3 className="font-display text-base font-bold text-gray-900">
              Order Summary
            </h3>
            <div className="space-y-2 text-sm">
              <SummaryRow label={`Item total (${cart.count} items)`} value={`₹${selectedTotals.mrp.toLocaleString()}`} />
              <SummaryRow label="Discount" value={`-₹${selectedTotals.discount.toLocaleString()}`} tone="green" />
              <SummaryRow label="Delivery" value={deliveryUnlocked ? "FREE" : "₹49"} tone={deliveryUnlocked ? "green" : "default"} />
              {appliedCoupon ? (
                <SummaryRow label={`Coupon (${appliedCoupon.code})`} value={`-₹${appliedCoupon.value.toLocaleString()}`} tone="purple" />
              ) : (
                <SummaryRow label="Coupon" value="Apply coupon" tone="purple" onClick={() => setCouponOpen(true)} />
              )}
            </div>
            <div className="border-t border-dashed border-gray-200 pt-3 flex justify-between items-center">
              <span className="font-display font-bold text-gray-900">Grand Total</span>
              <span
                data-testid="cart-total"
                className="font-display text-xl font-extrabold text-gray-900"
              >
                ₹{grandTotal.toLocaleString()}
              </span>
            </div>
            {totalSaved > 0 && (
              <div className="rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100 px-3 py-1.5 text-xs font-semibold text-center">
                🎉 You save ₹{totalSaved.toLocaleString()} on this order
              </div>
            )}

            <label className="flex items-start gap-2 text-xs text-gray-600 mt-2">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 accent-[#4B2AAD] h-4 w-4"
                data-testid="cart-terms"
              />
              I have read and agree to the{" "}
              <Link to="/policy/terms" className="text-[#4B2AAD] font-semibold">
                Terms &amp; Conditions
              </Link>
            </label>

            <button
              type="button"
              onClick={proceed}
              data-testid="cart-checkout-btn"
              className="w-full dyntra-btn-primary py-3 text-base"
            >
              <Lock className="h-4 w-4" />
              {user ? "Proceed to Checkout" : "Login to Continue →"}
            </button>

            <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-500">
              <span className="inline-flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5" /> Secure payments. Your data is safe with us.
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {["VISA", "Master", "RuPay", "UPI", "Paytm"].map((p) => (
                <span
                  key={p}
                  className="rounded-md border border-gray-200 bg-white px-2 py-1 text-[10px] font-bold text-gray-500"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
        </aside>
      </div>

      <Dialog open={dcrDialog} onOpenChange={setDcrDialog}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              Dental Council Registration ID
            </DialogTitle>
            <DialogDescription>
              Verify your professional credentials by adding your DCR ID. This
              enables clinical pricing and prescription-only products.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              DCR ID
            </label>
            <input
              value={dcrInput}
              onChange={(e) => setDcrInput(e.target.value)}
              placeholder="e.g. DCI-1234567"
              className="w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm"
              data-testid="cart-dcr-input"
            />
            <div className="flex justify-end gap-2">
              <button className="dyntra-btn-ghost" onClick={() => setDcrDialog(false)}>
                Cancel
              </button>
              <button
                className="dyntra-btn-primary"
                onClick={saveDcr}
                data-testid="cart-dcr-save"
              >
                Save DCR ID
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function CartRow({ item, checked, onToggle, onQty, onRemove, onMove }) {
  const p = item.product;
  const savePct =
    p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;
  return (
    <div
      data-testid={`cart-item-${p.id}`}
      className="p-4 grid grid-cols-1 md:grid-cols-[auto_88px_1fr_auto] gap-3 items-start"
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="h-4 w-4 mt-2 accent-[#4B2AAD]"
        data-testid={`cart-select-${p.id}`}
      />
      <div className="h-20 w-20 rounded-xl bg-[#F5F3FB] overflow-hidden shrink-0">
        <img
          src={p.image || "/product-placeholder.svg"}
          alt=""
          className="h-full w-full object-contain p-1"
          onError={(e) => {
            e.currentTarget.src = "/product-placeholder.svg";
          }}
        />
      </div>
      <div className="min-w-0">
        <Link
          to={`/product/${p.id}`}
          className="text-sm font-bold text-gray-900 hover:text-[#4B2AAD] line-clamp-2"
        >
          {p.name}
        </Link>
        <p className="text-[11px] text-gray-500 mt-0.5">
          {p.brand ? p.brand : "\u00A0"}
          {p.brand ? " · " : ""}SKU: {p.slug || p.id}
        </p>
        <div className="mt-1 flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 font-semibold border border-emerald-100">
            ● {p.in_stock ? "In Stock" : "Out of Stock"}
          </span>
          <span className="inline-flex items-center gap-1 text-[#4B2AAD] font-semibold">
            <Truck className="h-3.5 w-3.5" /> Delivery by Fri
          </span>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={onMove}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#4B2AAD]"
            data-testid={`cart-move-wishlist-${p.id}`}
          >
            <Heart className="h-3.5 w-3.5" /> Move to Wishlist
          </button>
          <button
            onClick={onRemove}
            className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-600"
            data-testid={`cart-remove-${p.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
        </div>
      </div>
      <div className="text-right">
        <div className="flex items-baseline gap-1 justify-end flex-wrap">
          <span className="font-display text-base font-extrabold text-gray-900">
            ₹{(p.price * item.quantity).toLocaleString()}
          </span>
        </div>
        {p.mrp > p.price && (
          <p className="text-xs text-gray-400 line-through">
            ₹{(p.mrp * item.quantity).toLocaleString()}
          </p>
        )}
        {savePct > 0 && (
          <p className="text-[11px] font-bold text-[#4B2AAD]">Save {savePct}%</p>
        )}
        <div className="mt-2 inline-flex items-center rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() => onQty(item.quantity - 1)}
            className="px-2 py-1 hover:bg-gray-50"
            data-testid={`cart-qty-minus-${p.id}`}
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span className="w-8 text-center text-sm font-bold">
            {item.quantity}
          </span>
          <button
            onClick={() => onQty(item.quantity + 1)}
            className="px-2 py-1 hover:bg-gray-50"
            data-testid={`cart-qty-plus-${p.id}`}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function YouMayAlsoLike({ items, idx, onPrev, onNext }) {
  if (!items || items.length === 0) return null;
  const visible = items.slice(idx, idx + 6);
  return (
    <section className="rounded-2xl border border-gray-100 bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-base font-bold text-gray-900">
          You may also like
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={onPrev}
            className="h-8 w-8 rounded-full border border-gray-200 flex items-center justify-center hover:border-[#4B2AAD] hover:text-[#4B2AAD]"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={onNext}
            className="h-8 w-8 rounded-full border border-gray-200 flex items-center justify-center hover:border-[#4B2AAD] hover:text-[#4B2AAD]"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {visible.map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>
    </section>
  );
}

function SummaryRow({ label, value, tone, onClick }) {
  const toneCls =
    tone === "green"
      ? "text-emerald-600 font-semibold"
      : tone === "purple"
        ? "text-[#4B2AAD] font-semibold"
        : "text-gray-900 font-semibold";
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-500">{label}</span>
      {onClick ? (
        <button onClick={onClick} className={`${toneCls} hover:underline`}>
          {value}
        </button>
      ) : (
        <span className={toneCls}>{value}</span>
      )}
    </div>
  );
}

function Usp({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="h-9 w-9 rounded-full bg-[#F3EFFC] flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-[#4B2AAD]" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-bold text-gray-900 leading-tight truncate">
          {title}
        </p>
        <p className="text-[10px] text-gray-500 leading-tight truncate">
          {sub}
        </p>
      </div>
    </div>
  );
}
